import React from 'react';
import { Dialog, Button, Typography, CircularProgress, Box, Stack } from '@mui/material';
import ReactDiffViewer from 'react-diff-viewer-continued';

interface WorkflowDiffModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (id: string) => void;
  originalWorkflow: any;
  updatedWorkflow: any;
  isEditing?: boolean;
  loading?: boolean;
}

const WorkflowDiffModal: React.FC<WorkflowDiffModalProps> = ({
  open,
  onClose,
  onConfirm,
  originalWorkflow,
  updatedWorkflow,
  isEditing = false,
  loading = false
}) => {
  const formatJson = (json: any) => {
    try {
      return JSON.stringify(json, null, 2);
    } catch (error) {
      return '';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
    >
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {isEditing ? "Review Workflow Changes" : "Review New Workflow"}
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1">
            {isEditing ? 'Changes to be saved:' : 'New workflow to be created:'}
          </Typography>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size="large" />
          </Box>
        ) : (
          <Box sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1, overflow: 'hidden' }}>
            <ReactDiffViewer
              oldValue={formatJson(originalWorkflow)}
              newValue={formatJson(updatedWorkflow)}
              splitView={true}
              useDarkTheme={false}
              hideLineNumbers={false}
              leftTitle={isEditing ? "Current Version" : "Empty Workflow"}
              rightTitle={isEditing ? "New Version" : "New Workflow"}
              styles={{
                contentText: {
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  lineHeight: '1.5',
                },
                diffContainer: {
                  maxHeight: '60vh',
                  overflow: 'auto',
                },
                titleBlock: {
                  backgroundColor: '#f5f5f5',
                  padding: '8px 16px',
                  borderBottom: '1px solid #e8e8e8',
                },
                line: {
                  padding: '4px 8px',
                },
                highlightedLine: {
                  backgroundColor: 'rgba(206, 0, 179, 0.1)',
                },
                highlightedGutter: {
                  backgroundColor: 'rgba(206, 0, 179, 0.05)',
                },
                gutter: {
                  padding: '0 8px',
                  backgroundColor: '#fafafa',
                  borderRight: '1px solid #e8e8e8',
                },
              }}
            />
          </Box>
        )}
        
        <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', mt: 3 }}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={() => onConfirm(updatedWorkflow.id)}
            disabled={loading}
          >
            {isEditing ? 'Save Changes' : 'Create Workflow'}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
};

export default WorkflowDiffModal;